// Embedded, AO's own AI panel is hidden and the shell owns the single assistant - ADR-012 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/012-ai-assistant-extraction.md
// AO registers a provider supplying the active page's context and actions, and calls its own
// stateless completion endpoint per turn: the shell holds the conversation across products, AO's
// backend, prompts and tools are reused as-is.
//
// Only page-specific tools are bridged. The in-panel built-ins (render_table, render_chart,
// capture_screenshot, navigate) draw into AO's own panel and have no shell equivalent yet.
import { watch } from 'vue';
import axios from 'axios';
import { useAiStore } from '@/stores/ai';
import { isEmbedded } from '@/webshell-embed/embedded';
import { APP_KEY, shellBridge } from '@/webshell-embed/platform-bridge';

// Matches the composable's wait for AO's own panel — long enough for a big scenario to load.
const CONTEXT_READY_TIMEOUT_MS = 15000;

// The active page's actions, refreshed by the useAiAgentActions composable on mount.
let currentActions = [];
let registered = false;

// Returns the published array so the caller can hand it back to clearShellAssistantActions().
export function setShellAssistantActions(actions) {
  currentActions = Array.isArray(actions) ? actions : [];
  return currentActions;
}

// Withdraw a page's actions when it unmounts, so the shell stops offering handlers whose stores are
// gone. Identity-checked: were the incoming page ever to publish before the outgoing one unmounts,
// a blind clear would wipe the new page's tools instead.
export function clearShellAssistantActions(actions) {
  if (currentActions === actions) currentActions = [];
}

// The active page's context, read from the store at call time.
function pageContext(aiStore) {
  const ctx = aiStore.sidePanel?.context || {};
  const display = Array.isArray(ctx.contextDisplay)
    ? ctx.contextDisplay.filter(Boolean).join(' › ')
    : '';
  // A throwing context builder must not fail the turn — the assistant still answers, just without the
  // page's data. Logged because standalone lets it propagate, so embedded is the only place where it
  // is invisible, and an empty context looks exactly like a page that publishes none.
  let data = {};
  try {
    data = aiStore.sidePanel?.contextFactory?.() || {};
  } catch (err) {
    console.error('[webshell-embed] page context builder threw', err);
    data = {};
  }
  return { section: ctx.section || null, display, data };
}

// A page registers its context in mounted() but fills it from an async created(), signalling with
// setContextReady. In between contextFactory() returns a half-built object, so hold the turn until
// the page says its data is there — the same gate the composable applies to AO's own panel. Nothing
// to wait for on a page that registers no context; it gets the base prompt either way. Falls
// through on timeout rather than failing the turn: a thin answer beats none.
function waitForContextReady(aiStore) {
  if (!aiStore.sidePanel?.context || aiStore.sidePanel.contextReady) return Promise.resolve();
  return new Promise(resolve => {
    let timer;
    const stop = watch(
      () => aiStore.sidePanel.contextReady,
      ready => {
        if (!ready) return;
        clearTimeout(timer);
        stop();
        resolve();
      }
    );
    timer = setTimeout(() => {
      stop();
      resolve();
    }, CONTEXT_READY_TIMEOUT_MS);
  });
}

// Register AO's single assistant provider with the shell bridge. Idempotent; no-op when standalone.
export function registerShellAssistantProvider() {
  if (registered || !isEmbedded()) return;
  const bridge = shellBridge();
  if (!bridge) return;

  const aiStore = useAiStore();

  bridge.assistant.registerProvider(APP_KEY, {
    getContext() {
      return pageContext(aiStore);
    },
    getActions() {
      return currentActions;
    },
    // The shell's `context` argument is deliberately ignored: it was read before the wait below,
    // and by the time the request goes out the page may have finished loading — or the user may
    // have navigated on. The store is authoritative at send time.
    async sendTurn({ messages, toolSchemas }) {
      await waitForContextReady(aiStore);
      const context = pageContext(aiStore);
      // AO's own authenticated backend; embedded, axios.baseURL points at AO's origin (create-app.js).
      const { data } = await axios.post('/api/ai-assistant/completions', {
        messages,
        section: context.section,
        context: context.data,
        clientToolSchemas: toolSchemas || [],
      });
      return { content: data.response, pendingToolCall: data.pendingToolCall || null };
    },
  });
  registered = true;
}

// Withdraw the provider when AO unmounts. The shell's assistant store outlives AO's app instance,
// so without this the shell keeps calling a provider whose stores are gone.
export function unregisterShellAssistantProvider() {
  shellBridge()?.assistant.unregister(APP_KEY);
  currentActions = [];
  registered = false;
}
